/**
 * Resolve the onboarding record a page should display from the loaded list.
 *
 * When a specific `onboardingId` (route uuid or legacy integer id) is requested,
 * return the matching record, or `null` when it isn't in the list.
 *
 * `allowFirstFallback` re-enables the ENG-1465 behavior: a multi-trust flow can
 * hide the parent intake on the backend and leave only derived children in the
 * list, so a *self-view* navigation to the (now hidden) parent uuid falls back to
 * the first record. This is safe ONLY for self-view, where the list is the user's
 * own. For a cross-user viewer (admin/advisor opening a client's page) the list is
 * the *viewer's* own, so a first-record fallback would leak the viewer's unrelated
 * trust (ENG-2726) — callers pass `allowFirstFallback: false` there and redirect on
 * `null`. The correct record is loaded by fetching the owner's onboardings (see
 * refreshOnboardings + the route's ?client_id scope).
 *
 * With no `onboardingId`, there is nothing to disambiguate, so return `null`.
 *
 * @param {Array<{uuid?: string, id?: number|string}>} onboardings
 * @param {string|number|null|undefined} onboardingId
 * @param {{ allowFirstFallback?: boolean }} [options]
 * @returns {object|null}
 */
export function resolveOnboarding(onboardings, onboardingId, { allowFirstFallback = false } = {}) {
    const list = onboardings || [];
    if (!onboardingId) return null;
    const found = list.find(o => o.uuid === onboardingId || String(o.id) === String(onboardingId));
    if (found) return found;
    if (allowFirstFallback && list.length > 0) return list[0];
    return null;
}

/**
 * The owner uuid a cross-user view was opened for, read from the route's
 * ?client_id (the app-wide convention for "the client a viewer is acting on",
 * shared with the scenario/strategy/calculator flows). Vue Router yields an array
 * for a repeated query key, so collapse to the first value; returns null for
 * self-view (no ?client_id). Used both to scope the onboardings fetch and to gate
 * the self-view-only first-record fallback above.
 *
 * @param {{ query?: Record<string, string|string[]> }|null|undefined} route
 * @returns {string|null}
 */
export function clientIdFromRoute(route) {
    const client = route?.query?.client_id;
    const value = Array.isArray(client) ? client[0] : client;
    return value || null;
}

/**
 * Is this onboarding the viewer's OWN record?
 *
 * Gates every "we noticed your details changed — update your profile?" behaviour in
 * the intake wizard: those prompts read and write the *logged-in* user's profile, so
 * offering them while an advisor/admin fills a client's intake asks the viewer to
 * overwrite their own name, address, DOB and SSN with the client's (ENG-2793).
 *
 * Fails **closed**: only a positively matching uuid pair counts as self-view, so a
 * payload shape that omits the nested user (the slim clients-list summary rows) or a
 * viewer with no uuid yields false and the prompt simply stays hidden. The previous
 * check compared integer `user.id`s and failed *open* — with either id absent,
 * `undefined !== undefined` is false, so a cross-user intake read as self-view. Route
 * ids are uuids everywhere now (ENG-2726), so compare uuids.
 *
 * A route `?client_id` that isn't the viewer is decisive on its own: ownerQuery only
 * appends it for a cross-user navigation, so its presence means "acting on a client"
 * even before the record loads.
 *
 * @param {{ user?: { uuid?: string } }|null|undefined} onboarding
 * @param {string|null|undefined} viewerUuid the logged-in (or impersonated) user's uuid
 * @param {{ query?: Record<string, string|string[]> }|null|undefined} [route]
 * @returns {boolean}
 */
export function isSelfOwnedOnboarding(onboarding, viewerUuid, route) {
    if (!viewerUuid) return false;
    const clientId = clientIdFromRoute(route);
    if (clientId && clientId !== viewerUuid) return false;
    const ownerUuid = onboarding?.user?.uuid;
    return Boolean(ownerUuid) && ownerUuid === viewerUuid;
}

/**
 * Route-`query` fragment that carries the onboarding owner across a cross-user
 * navigation. Returns `{ client_id: <owner> }` only when a viewer opens someone
 * else's record (owner set and different from the viewer); self-view returns `{}`
 * so no ?client_id is appended. Spread into a route location's `query`.
 *
 * Centralizing this is the point: every navigation into document-review /
 * project-detail / onboarding-form must forward the owner, or the target page
 * loads the viewer's own onboardings and shows the wrong trust (ENG-2726). One
 * helper keeps all call sites consistent and prevents missed hops. `client_id` is
 * the same param the scenario/strategy/calculator flows already use for the client
 * a viewer is acting on.
 *
 * @param {string|null|undefined} ownerUuid  the record's owner uuid
 * @param {string|null|undefined} viewerUuid the logged-in user's uuid
 * @returns {{ client_id: string } | {}}
 */
export function ownerQuery(ownerUuid, viewerUuid) {
    return ownerUuid && ownerUuid !== viewerUuid ? { client_id: ownerUuid } : {};
}

/**
 * The ?client_id owner uuid parsed from a URL query string (e.g.
 * window.location.search). Same semantics as clientIdFromRoute but for a raw
 * search string, so the store layer can read it without a Vue route object.
 * Returns null when absent.
 *
 * @param {string|null|undefined} search
 * @returns {string|null}
 */
export function clientIdFromSearch(search) {
    try {
        return new URLSearchParams(search || '').get('client_id') || null;
    } catch (e) {
        return null;
    }
}

// A cross-user view (?client_id) must never fall back to seeding the viewer's
// own identity into the client's POC form (ENG-2987).
export function pocSeedSource(owner, viewer, search) {
    if (owner) return owner;
    return clientIdFromSearch(search) ? null : viewer || null;
}

function userIdentity(user) {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    if (name && user?.email) return `${name} (${user.email})`;
    return name || user?.email || '';
}

/**
 * Build the Slack message emitted when an intake is completed. In a cross-user
 * flow the viewer is the person doing the work and the onboarding owner is the
 * applicant, so include both identities. Self-service messages retain their
 * existing wording.
 */
export function onboardingCompletionMessage(viewer, owner, onboardingType) {
    const actor = userIdentity(viewer);
    const applicant = userIdentity(owner);
    const sameUser = Boolean(
        (viewer?.uuid && owner?.uuid && viewer.uuid === owner.uuid) ||
            (viewer?.email && owner?.email && viewer.email === owner.email),
    );
    const behalf = applicant && !sameUser ? ` on behalf of ${applicant}` : '';
    return [actor, `finished ${onboardingType}${behalf}`].filter(Boolean).join(' ');
}

/**
 * Scope an onboarding write payload to the given owner: sets `user_id` to `ownerId`
 * unless the caller already provided one (explicit user_id always wins). Returns the
 * payload unchanged when there is no owner to apply. Used to centrally route
 * cross-user onboarding mutations to the client, not the viewer (ENG-2726).
 *
 * @param {object} payload
 * @param {string|null|undefined} ownerId
 * @returns {object}
 */
export function withOwnerScope(payload, ownerId) {
    if (ownerId && payload && typeof payload === 'object' && !('user_id' in payload)) {
        return { ...payload, user_id: ownerId };
    }
    return payload;
}

/**
 * The product label whose onboarding components an intake's answers belong to.
 *
 * A descendants-only SLANT is relabelled to the `ngt` product once its profile completes so
 * ops reads it as a non-grantor trust, but the answers it holds are still the SLANT flow's:
 * the NGT profile has no beneficiary-class step and offers a trustee and a spouse beneficiary
 * that ProductRenderer.slant_ngt overrides or rejects, and OnboardingDetailsNgt never fetches
 * the agreement. Keep such records on the components that match the document they generate.
 *
 * The class is spelled out rather than imported from @/constants: tests/onboarding-resolve.spec.ts
 * loads this module from plain Node, and this module stays free of app-config imports so it
 * keeps behaving identically there (see services/viewport.js for the same reasoning).
 */
export function onboardingFlowLabel(onboarding) {
    const label = onboarding?.selected_product?.product_label || '';
    if (label === 'ngt' && onboarding?.slant_spouse === 'none') return 'slant';
    return label;
}
